import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface FCMNotification {
  from: string;
  collapseKey?: string;
  messageId: string;
  notification: {
    title: string;
    body: string;
  };
  data: {
    patient_id?: string;
    type: string;
    [key: string]: any;
  };
}

export interface NotificationBanner {
  id: string;
  type: string;
  title: string;
  message: string;
  patient_id?: string;
  timestamp: Date;
  dismissed: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationStateService {
  // BehaviorSubject to hold the latest FCM notification
  private fcmNotificationSubject = new BehaviorSubject<FCMNotification | null>(null);
  
  // BehaviorSubject to hold active notification banners
  private notificationBannersSubject = new BehaviorSubject<NotificationBanner[]>([]);
  
  // Observable streams
  public fcmNotification$: Observable<FCMNotification | null> = this.fcmNotificationSubject.asObservable();
  public notificationBanners$: Observable<NotificationBanner[]> = this.notificationBannersSubject.asObservable();

  constructor() {}

  /**
   * Emit a new FCM notification
   */
  emitFCMNotification(notification: FCMNotification): void {
    this.fcmNotificationSubject.next(notification);
    
    // Also add to banners if it's a followup_reminder
    if (notification.data.type === 'followup_reminder') {
      this.addNotificationBanner({
        id: notification.messageId,
        type: notification.data.type,
        title: notification.notification.title,
        message: notification.notification.body,
        patient_id: notification.data.patient_id,
        timestamp: new Date(),
        dismissed: false,
      });
    }
  }

  /**
   * Add a notification banner
   */
  private addNotificationBanner(banner: NotificationBanner): void {
    const currentBanners = this.notificationBannersSubject.value;
    
    // Check if banner already exists for this patient
    const existingIndex = currentBanners.findIndex(
      b => b.patient_id === banner.patient_id && b.type === banner.type
    );
    
    if (existingIndex === -1) {
      // Add new banner
      this.notificationBannersSubject.next([...currentBanners, banner]);
    } else {
      // Update existing banner
      const updatedBanners = [...currentBanners];
      updatedBanners[existingIndex] = banner;
      this.notificationBannersSubject.next(updatedBanners);
    }
  }

  /**
   * Dismiss a notification banner
   */
  dismissBanner(bannerId: string): void {
    const currentBanners = this.notificationBannersSubject.value;
    const updatedBanners = currentBanners.map(banner =>
      banner.id === bannerId ? { ...banner, dismissed: true } : banner
    );
    this.notificationBannersSubject.next(updatedBanners);
  }

  /**
   * Get active banners for a specific patient
   */
  getBannersForPatient(patientId: string): NotificationBanner[] {
    return this.notificationBannersSubject.value.filter(
      banner => banner.patient_id === patientId && !banner.dismissed
    );
  }

  /**
   * Clear all banners
   */
  clearAllBanners(): void {
    this.notificationBannersSubject.next([]);
  }

  /**
   * Clear banners for a specific patient
   */
  clearBannersForPatient(patientId: string): void {
    const currentBanners = this.notificationBannersSubject.value;
    const filteredBanners = currentBanners.filter(
      banner => banner.patient_id !== patientId
    );
    this.notificationBannersSubject.next(filteredBanners);
  }
}

